import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Stack, TextField } from '@mui/material';
import { debounce } from 'lodash';

import { CustomPopperComponent } from '../AutoCompleteSingleField';

import { OptionItem } from './OptionItem';
import { AutoCompleteGoogleMapsWithDetailProps, PlaceType } from './type';

import { CloseCircleIcon } from '~assets/icons';
import {
  mapGooglePeriodsToOperatingHours,
  parseGooglePlaceInfoForCreateAddress,
} from '~utils/google.util';

export const AutoCompleteGoogleMapsWithDetail = ({
  onSelect,
  textFieldProps = {},
  fakeMapRef,
  autocompleteServiceRef,
  placesServiceRef,
  ...restProps
}: AutoCompleteGoogleMapsWithDetailProps) => {
  const [value, setValue] = useState<PlaceType | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<readonly PlaceType[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  const fetch = useMemo(
    () =>
      debounce(
        (
          request: { input: string },
          callback: (results: PlaceType[] | null) => void,
        ) => {
          setIsFetching(true);
          autocompleteServiceRef.current?.getPlacePredictions(
            request,
            callback,
          );
        },
        400,
      ),
    [autocompleteServiceRef],
  );

  const handleChange = async (value: PlaceType | null) => {
    if (!placesServiceRef.current) return;
    setValue(value);
    if (value) {
      placesServiceRef.current.getDetails(
        { placeId: value.place_id },
        (res) => {
          const placeInfo = parseGooglePlaceInfoForCreateAddress(
            res?.address_components,
          );
          const lat = res?.geometry?.location?.lat() ?? 0;
          const lng = res?.geometry?.location?.lng() ?? 0;

          const operatingHours = mapGooglePeriodsToOperatingHours(
            res?.opening_hours?.periods,
          );

          onSelect({
            name: res?.formatted_address || value.description,
            id: value.place_id,
            location: {
              lat,
              lng,
              infoAddress: {
                state: placeInfo.state,
                country: placeInfo.country,
                countryCode: placeInfo.countryCode,
                zip: placeInfo.zip,
                city: placeInfo.city,
              },
              operatingHours,
            },
          });
        },
      );
    }
  };

  /**
   * Searching input
   */
  useEffect(() => {
    if (!autocompleteServiceRef.current) return;

    let active = true;

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return;
    }

    fetch({ input: inputValue }, (results) => {
      if (active) {
        setIsFetching(false);
        let newOptions: readonly PlaceType[] = [];

        if (value) {
          newOptions = results?.some(
            (result) => result.place_id === value.place_id,
          )
            ? []
            : [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch, autocompleteServiceRef]);

  return (
    <div>
      <div ref={fakeMapRef} />
      <Autocomplete
        id="google-map-location"
        loading={isFetching}
        fullWidth
        filterOptions={(options) => options}
        getOptionLabel={(option) => option.description}
        options={options}
        value={value}
        noOptionsText="No location found"
        isOptionEqualToValue={(option, value) =>
          option.place_id === value.place_id
        }
        onChange={(_, newValue) => {
          handleChange(newValue);
        }}
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue);
        }}
        renderInput={(params) => (
          <TextField
            ref={params.InputProps.ref}
            {...params}
            {...textFieldProps}
            InputProps={{
              ...params.InputProps,
              ...textFieldProps.InputProps,
            }}
            inputProps={{
              ...params.inputProps,
              ...textFieldProps.inputProps,
            }}
          />
        )}
        renderOption={(props, option) => (
          <div key={option?.place_id}>
            <OptionItem option={option} props={props} />
          </div>
        )}
        clearIcon={
          <Stack marginRight={0.5}>
            <CloseCircleIcon />
          </Stack>
        }
        popupIcon={null}
        PopperComponent={CustomPopperComponent}
        {...restProps}
      />
    </div>
  );
};
